
import React, { useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation } from '@tanstack/react-router';
import { TFunction } from 'react-i18next';
import {
  ModalView, Stack, Text, FormLabel, Button, Form, Information, SelectControl, Card, HelpLink,
  TextFieldControl, RadioButtonGroup, withTranslation, Spinner, Tooltip, Icon,
} from '@piwikpro/ui-components';
import { FormikProps, FormikActions } from 'formik';
import { DomainSetting } from '@piwikpro/container-settings-crate/redux/appDomainSettings';
import { withInject, view } from '@piwikpro/platform';
import { RouterService } from '@piwikpro/router-crate';
import { ContainerSettings } from '@piwikpro/container-settings-crate/ContainerSettings';
import { ContainerSettingsState } from '@piwikpro/container-settings-crate/redux';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import {
  PIWIK_BUILT_IN_DOMAINS, Domain, DomainTypes,
} from '../../../redux/list';
import { DomainsService } from '../../../DomainsService';
import FailMessage from '../FailMessage';
import { DomainsState } from '../../../redux';

const SETTING_NAME_TO_FORM_INPUT_MAPPING = {
  firstPartyCollector: 'fpc',
  standardPiwikProApiDomain: 'ui_apis_domain',
  standardStaticResourceDomain: 'static_resources_domain',
  standardTrackingDomain: 'tracking_domain',
  piwikProApiDomain: 'ui_apis_domain',
  staticResourcesAddressDomain: 'static_resources_domain',
  staticResourcesAddressContainerJsPath: 'container_js_path',
  staticResourcesAddressTrackingScriptPath: 'tracking_script_path',
  trackingAddressDomain: 'tracking_domain',
  trackingAddressTrackingEndpointsPath: 'tracking_endpoint_path',
};

const STANDARD_FORM_FIELDS = ['firstPartyCollector', 'standardPiwikProApiDomain', 'standardStaticResourceDomain', 'standardTrackingDomain'];
const FPC_FORM_FIELDS = ['firstPartyCollector', 'piwikProApiDomain', 'staticResourcesAddressDomain', 'trackingAddressDomain', 'staticResourcesAddressContainerJsPath', 'staticResourcesAddressTrackingScriptPath', 'trackingAddressTrackingEndpointsPath'];

const mapStateToProps = (
  state: DomainsState &
  ContainerSettingsState &
  { session: SessionState },
) => {
  const { app, appDefault } = state.containerSettings;
  const { value: fpc } = app.domainSettings.fpc;

  return {
    domainsList: state.domains.list.list,
    fpc: typeof fpc === 'object' ? fpc.is_enabled : fpc,
    containerJsPath: app.domainSettings.containerJsPath.value,
    staticResourcesDomain: app.domainSettings.staticResourcesDomain.value,
    trackingDomain: app.domainSettings.trackingDomain.value,
    trackingScriptPath: app.domainSettings.trackingScriptPath.value,
    trackingEndpointPath: app.domainSettings.trackingEndpointPath.value,
    uiApisDomain: app.domainSettings.uiApisDomain.value,
    defaultContainerJsPath: appDefault.domainSettings.containerJsPath.value,
    defaultStaticResourcesDomain: appDefault.domainSettings.staticResourcesDomain.value,
    defaultTrackingDomain: appDefault.domainSettings.trackingDomain.value,
    defaultTrackingScriptPath: appDefault.domainSettings.trackingScriptPath.value,
    defaultTrackingEndpointPath: appDefault.domainSettings.trackingEndpointPath.value,
    defaultUiApisDomain: appDefault.domainSettings.uiApisDomain.value,
    organizationName: state.session.organizationName,
    isLoading: Object.values(app.domainSettings).some(setting => setting.isLoading)
      || Object.values(appDefault.domainSettings).some(setting => setting.isLoading)
      || state.domains.list.isLoading,
    failed:
      Object.values(app.domainSettings).some(setting => setting.failed)
      || Object.values(appDefault.domainSettings).some(setting => setting.failed)
      || state.domains.list.failed,
  };
};

const connector = connect(mapStateToProps);

interface InjectedProps {
  containerSettings: ContainerSettings
  domains: DomainsService
  routerService: RouterService
  t: TFunction
}

type FormValue = Partial<{
  standardPiwikProApiDomain: string
  standardStaticResourceDomain: string
  standardTrackingDomain: string
  piwikProApiDomain: string
  staticResourcesAddressDomain: string
  staticResourcesAddressContainerJsPath: string
  staticResourcesAddressTrackingScriptPath: string
  trackingAddressDomain: string
  trackingAddressTrackingEndpointsPath: string
  firstPartyCollector: string
}>

export interface Props extends ConnectedProps<typeof connector>, InjectedProps {
  appId: string
}

const filterDomain = (domainsList: Domain[], domainType: DomainTypes, selectedDomainName: string) => domainsList?.filter(
  ({ types }) => types.includes(domainType),
)
  .filter(({ domain }) => domain === selectedDomainName || !PIWIK_BUILT_IN_DOMAINS.includes(domain))
  .map(
    ({ domain }) => ({
      name: domain,
      id: domain,
      value: domain,
    }),
  );

const wrapContentWithCustomHeightContainer = (
  children: React.ReactElement,
  fpc: string,
): React.ReactElement => (
  <div style={{ height: fpc === 'standard' ? '460px' : '760px', alignContent: 'center' }}>
    {children}
  </div>
);

export const DomainsTrackingEnterpriseForm: React.FC<Props> = ({
  domainsList,
  containerSettings,
  domains,
  uiApisDomain,
  staticResourcesDomain,
  trackingDomain,
  trackingEndpointPath,
  trackingScriptPath,
  containerJsPath,
  fpc,
  appId,
  isLoading,
  failed,
  defaultContainerJsPath,
  defaultStaticResourcesDomain,
  defaultTrackingDomain,
  defaultTrackingScriptPath,
  defaultTrackingEndpointPath,
  defaultUiApisDomain,
  routerService,
  t,
}) => {
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          domains.getDomainsList(),
          containerSettings.getDefaultAppSettings(appId),
        ]);
        // eslint-disable-next-line no-empty
      } catch { }
    };

    fetchData();
  }, []);

  const handleModalClose = () => {
    routerService.push(location.pathname.replace(/\/m\/edit-tracking-domains$/, ''));
  };

  const uiAndAPIs = filterDomain(domainsList, 'ui_apis', uiApisDomain as string);
  const staticTrackingResources = filterDomain(domainsList, 'static_resources', staticResourcesDomain as string);
  const trackingEndpoints = filterDomain(domainsList, 'tracking_dynamic_endpoints', trackingDomain as string);

  const editDomainsFormInitialValues: FormValue = {
    standardPiwikProApiDomain: uiApisDomain as string,
    standardStaticResourceDomain: staticResourcesDomain as string,
    standardTrackingDomain: trackingDomain as string,
    piwikProApiDomain: uiApisDomain as string,
    staticResourcesAddressDomain: staticResourcesDomain as string,
    staticResourcesAddressContainerJsPath: containerJsPath as string,
    staticResourcesAddressTrackingScriptPath: trackingScriptPath as string,
    trackingAddressDomain: trackingDomain as string,
    trackingAddressTrackingEndpointsPath: trackingEndpointPath as string,
    firstPartyCollector: fpc ? 'first-party' : 'standard',
  };
  const getChangedFormValues = (values: FormValue, target: FormValue) => {
    const changedValues = {} as FormValue;

    Object.keys(target).forEach((key) => {
      if (target[key] !== values[key]) {
        changedValues[key] = values[key];
      }
    });

    return changedValues;
  };

  const isFormModified = (values: FormValue, target: FormValue, allowEmpty: boolean) => {
    const fpcEnabled = (values.firstPartyCollector ?? target.firstPartyCollector) === 'first-party';
    const changedValues = getChangedFormValues(values, target);

    if (allowEmpty && Object.values(changedValues).some(value => value === '')) {
      return false;
    }

    const filteredValues = Object.keys(changedValues)
      .filter(key =>
        (fpcEnabled ? FPC_FORM_FIELDS.includes(key) : STANDARD_FORM_FIELDS.includes(key)));

    return filteredValues.length > 0;
  };

  const defaultFormValues: FormValue = {
    standardPiwikProApiDomain: defaultUiApisDomain as string,
    standardStaticResourceDomain: defaultStaticResourcesDomain as string,
    standardTrackingDomain: defaultTrackingDomain as string,
    piwikProApiDomain: defaultUiApisDomain as string,
    staticResourcesAddressDomain: defaultStaticResourcesDomain as string,
    staticResourcesAddressContainerJsPath: defaultContainerJsPath as string,
    staticResourcesAddressTrackingScriptPath: defaultTrackingScriptPath as string,
    trackingAddressDomain: defaultTrackingDomain as string,
    trackingAddressTrackingEndpointsPath: defaultTrackingEndpointPath as string,
  };

  const handleStandardRestoreDefault = (setFieldValue: FormikActions<FormValue>['setFieldValue']) => {
    setFieldValue('standardPiwikProApiDomain', defaultUiApisDomain);
    setFieldValue('standardStaticResourceDomain', defaultStaticResourcesDomain);
    setFieldValue('standardTrackingDomain', defaultTrackingDomain);
  };

  const handleFPCRestoreDefault = (setFieldValue: FormikActions<FormValue>['setFieldValue']) => {
    setFieldValue('piwikProApiDomain', defaultUiApisDomain);
    setFieldValue('staticResourcesAddressDomain', defaultStaticResourcesDomain);
    setFieldValue('trackingAddressDomain', defaultTrackingDomain);
    setFieldValue('staticResourcesAddressContainerJsPath', defaultContainerJsPath);
    setFieldValue('staticResourcesAddressTrackingScriptPath', defaultTrackingScriptPath);
    setFieldValue('trackingAddressTrackingEndpointsPath', defaultTrackingEndpointPath);
  };

  const handleSubmit = async (values: FormValue, {
    setSubmitting,
  }: FormikProps<FormValue>) => {
    try {
      const fpcEnabled = (values.firstPartyCollector ?? editDomainsFormInitialValues.firstPartyCollector) === 'first-party';
      const changedValues = getChangedFormValues(values, editDomainsFormInitialValues);
      const apiAttributes = Object.keys(changedValues).filter(
        key => (fpcEnabled ? FPC_FORM_FIELDS.includes(key) : STANDARD_FORM_FIELDS.includes(key)),
      ).map(key => ({
        [SETTING_NAME_TO_FORM_INPUT_MAPPING[key]]:
          key === 'firstPartyCollector' ? { is_enabled: changedValues[key] === 'first-party' } : changedValues[key],
      }));


      await containerSettings.updateAppSettings(
        appId,
        apiAttributes.map((obj) => {
          const key = Object.keys(obj)[0] as DomainSetting;
          const value = obj[key];

          return {
            id: key,
            attributes: {
              value,
            },
          };
        }),
      );
      handleModalClose();
      // eslint-disable-next-line no-empty
    } catch { }

    setSubmitting(false);
  };

  const onRadioButtonGroupChange = (setFieldValue, value) => {
    const currentValue = editDomainsFormInitialValues.firstPartyCollector;
    setFieldValue('firstPartyCollector', value);

    if (currentValue === 'first-party' && value === 'standard') {
      handleStandardRestoreDefault(setFieldValue);
    }
  };

  return (
    <Form
      id="edit-domain-setting-form"
      initialValues={editDomainsFormInitialValues}
      validationSchema={() => { }}
      onSubmit={handleSubmit}
      render={({ values, setFieldValue, submitForm }) => (
        <ModalView
          title={t('platform.domains.edit-domain-tracking.edit-modal-title')}
          onClose={handleModalClose}
          actions={(
            <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
              <Button
                text={t('platform.domains.edit-domain-tracking.cancel-button-text')}
                onClick={handleModalClose}
              />
              <Button
                onClick={() => submitForm()}
                disabled={!isFormModified(values, editDomainsFormInitialValues, true)}
                text={t('platform.domains.edit-domain-tracking.save-button-text')}
                appearance="primary"
              />
            </Stack>
          )}
        >
          {!isLoading && failed
            && wrapContentWithCustomHeightContainer(
              <FailMessage />, values.firstPartyCollector,
            )
          }
          {isLoading && !failed
            && wrapContentWithCustomHeightContainer(
              <Spinner type="centered" />, values.firstPartyCollector,
            )
          }
          {!failed && !isLoading && (
            <Stack vertical horizontalAlignment="middle">
              <Stack.Item fill>
                <Text as="p" weight={600} size={12} dataId="Label">
                  {t('platform.domains.edit-domain-tracking.choose-tracking-type')}
                </Text>
              </Stack.Item>
              <Stack.Item fill previousSpacing="narrow">
                <RadioButtonGroup
                  onChange={
                    ({ target: { value } }) => onRadioButtonGroupChange(setFieldValue, value)
                  }
                  value={values.firstPartyCollector}
                  name="firstPartyCollector"
                  options={[
                    {
                      id: 'standard',
                      label: t('platform.domains.edit-domain-tracking.standard-tracking-type'),
                      value: 'standard',
                    },
                    {
                      id: 'first-party',
                      label: t('platform.domains.edit-domain-tracking.fpc-tracking-type'),
                      value: 'first-party',
                    },
                  ]}
                />
              </Stack.Item>
              {
                values.firstPartyCollector !== 'standard' && (
                  <Stack.Item>
                    <Information type="warning">
                      {t('platform.domains.edit-domain-tracking.risk-information')}
                    </Information>
                  </Stack.Item>
                )
              }
              <Stack.Item fill>
                {values.firstPartyCollector === 'standard' ? (
                  <Card>
                    <Card.Section subdued padding="narrow">
                      <Stack vertical>
                        <Stack.Item>
                          <FormLabel htmlFor="standardPiwikProApiDomain" size="normal">
                            {t('platform.domains.app-domain-tracking.piwik-pro-api-domain')}
                          </FormLabel>
                        </Stack.Item>
                        <Stack.Item previousSpacing="narrow" fill>
                          <Tooltip content={uiAndAPIs.length < 2 && values.standardPiwikProApiDomain === uiAndAPIs[0].value ? t('platform.domains.edit-domain-tracking.piwik-pro-api-domain-tooltip') : ''}>
                            <SelectControl
                              name="standardPiwikProApiDomain"
                              disabled={uiAndAPIs.length < 2
                                && values.standardPiwikProApiDomain === uiAndAPIs[0].value
                              }
                              options={[{
                                id: 'ui-apis',
                                items: uiAndAPIs || [],
                              }]}
                            />
                          </Tooltip>
                        </Stack.Item>
                      </Stack>
                    </Card.Section>
                    <Card.Section subdued padding="narrow">
                      <Stack vertical>
                        <Stack.Item>
                          <FormLabel htmlFor="standardStaticResourceDomain" size="normal">
                            {t('platform.domains.app-domain-tracking.static-resources-domain')}
                          </FormLabel>
                        </Stack.Item>
                        <Stack.Item previousSpacing="narrow" fill>
                          <Tooltip content={staticTrackingResources.length < 2 && values.standardStaticResourceDomain === staticTrackingResources[0].value ? t('platform.domains.edit-domain-tracking.static-resources-domain-tooltip') : ''}>
                            <SelectControl
                              name="standardStaticResourceDomain"
                              // eslint-disable-next-line max-len
                              disabled={staticTrackingResources.length < 2 && values.standardStaticResourceDomain === staticTrackingResources[0].value
                              }
                              options={[{
                                id: 'static-resources',
                                items: staticTrackingResources || [],
                              }]}
                            />
                          </Tooltip>
                        </Stack.Item>
                        <Stack.Item previousSpacing="narrow">
                          <Stack spacing="narrow">
                            <Stack.Item>
                              <Icon color="yellow" name="exclamation-mark-circle" />
                            </Stack.Item>
                            <Stack.Item>
                              <Text as="p" size={12} dataId="Caption" color="InkLighter">
                                {t('platform.domains.edit-domain-tracking.static-resources-domain-warning')}
                              </Text>
                            </Stack.Item>
                          </Stack>
                        </Stack.Item>
                      </Stack>
                    </Card.Section>
                    <Card.Section subdued padding="narrow">
                      <Stack vertical>
                        <Stack.Item>
                          <FormLabel htmlFor="standardTrackingDomain" size="normal">
                            {t('platform.domains.app-domain-tracking.tracking-domain')}
                          </FormLabel>
                        </Stack.Item>
                        <Stack.Item previousSpacing="narrow" fill>
                          <Tooltip content={trackingEndpoints.length < 2 && values.standardTrackingDomain === trackingEndpoints[0].value ? t('platform.domains.edit-domain-tracking.tracking-endpoints-tooltip') : ''}>
                            <SelectControl
                              name="standardTrackingDomain"
                              disabled={trackingEndpoints.length < 2
                                && values.standardTrackingDomain === trackingEndpoints[0].value
                              }
                              options={[{
                                id: 'tracking-dynamic-endpoints',
                                items: trackingEndpoints || [],
                              }]}
                            />
                          </Tooltip>
                        </Stack.Item>
                      </Stack>
                    </Card.Section>
                    <Card.Section subdued padding="none">
                      <div style={{ padding: '8px' }}>
                        <Stack>
                          <Stack.Item fill />
                          <Stack.Item>
                            <Button
                              disabled={!isFormModified(values, defaultFormValues, false)}
                              onClick={() => handleStandardRestoreDefault(setFieldValue)}
                              appearance="simple"
                              text={t('platform.domains.edit-domain-tracking.restore-defaults-button-text')}
                            />
                          </Stack.Item>
                        </Stack>
                      </div>
                    </Card.Section>
                  </Card>
                ) : (
                  <Card>
                    <Card.Section subdued padding="narrow">
                      <Stack vertical>
                        <Stack.Item>
                          <FormLabel htmlFor="piwikProApiDomain" size="normal">
                            {t('platform.domains.app-domain-tracking.piwik-pro-api-domain')}
                          </FormLabel>
                        </Stack.Item>
                        <Stack.Item previousSpacing="narrow" fill>
                          <TextFieldControl name="piwikProApiDomain" />
                        </Stack.Item>
                      </Stack>
                    </Card.Section>
                    <Card.Section subdued padding="narrow">
                      <Stack vertical>
                        <Stack.Item>
                          <Text as="p" weight={600} dataId="Label">
                            {t('platform.domains.app-domain-tracking.static-resources-address')}
                          </Text>
                        </Stack.Item>
                        <Stack.Item previousSpacing="narrow" fill>
                          <Stack vertical spacing="narrow">
                            <Stack.Item fill previousSpacing="default">
                              <TextFieldControl name="staticResourcesAddressDomain" label={t('platform.domains.app-domain-tracking.static-resources-address-domain')} />
                            </Stack.Item>
                            <Stack.Item fill>
                              <TextFieldControl name="staticResourcesAddressContainerJsPath" label={t('platform.domains.app-domain-tracking.static-resources-address-container-js')} />
                            </Stack.Item>
                            <Stack.Item fill>
                              <TextFieldControl name="staticResourcesAddressTrackingScriptPath" label={t('platform.domains.app-domain-tracking.static-resources-address-tracking-script')} />
                            </Stack.Item>
                          </Stack>
                        </Stack.Item>
                      </Stack>
                    </Card.Section>
                    <Card.Section subdued padding="narrow">
                      <Stack vertical>
                        <Stack.Item>
                          <Text as="p" weight={600} dataId="Label">
                            {t('platform.domains.app-domain-tracking.tracking-address')}
                          </Text>
                        </Stack.Item>
                        <Stack.Item fill>
                          <Stack vertical spacing="narrow">
                            <Stack.Item fill>
                              <TextFieldControl name="trackingAddressDomain" label={t('platform.domains.app-domain-tracking.tracking-address-domain')} />
                            </Stack.Item>
                            <Stack.Item fill>
                              <TextFieldControl name="trackingAddressTrackingEndpointsPath" label={t('platform.domains.app-domain-tracking.tracking-address-endpoint-path')} />
                            </Stack.Item>
                          </Stack>
                        </Stack.Item>
                      </Stack>
                    </Card.Section>
                    <Card.Section subdued padding="none">
                      <div style={{ padding: '8px' }}>
                        <Stack>
                          <Stack.Item fill />
                          <Stack.Item>
                            <Button
                              appearance="simple"
                              onClick={() => handleFPCRestoreDefault(setFieldValue)}
                              disabled={!isFormModified(values, defaultFormValues, false)
                              }
                              text={t('platform.domains.edit-domain-tracking.restore-defaults-button-text')}
                            />
                          </Stack.Item>
                        </Stack>
                      </div>
                    </Card.Section>
                  </Card>
                )}
              </Stack.Item>
              <Stack.Item>
                <HelpLink href="https://help.piwik.pro/support/questions/what-is-the-difference-between-piwik-pro-client-side-tracking-piwik-pro-first-party-collector-server-side-and-google-tag-manager-server-side/">
                  Client-side tracking vs first-party collector (hybrid server-side)
                </HelpLink>
              </Stack.Item>
            </Stack>
          )}
        </ModalView>
      )}
    />
  );
};

export default connector(withInject<InjectedProps>({
  containerSettings: 'ContainerSettingsCrate.ContainerSettings',
  domains: 'DomainsCrate.DomainsService',
  routerService: 'RouterCrate.router',
})(
  view<Props, InjectedProps>({ role: 'USER', level: 'publish' }, false)(
    withTranslation(['administration'])(DomainsTrackingEnterpriseForm),
  ),
));
